<template>
  <label :class="['check', $attrs.class, { 'check--disabled': disabled }]"
    :style="$attrs.style">
    <span class="check__box">
      <input type="checkbox"
        :checked="modelValue"
        :indeterminate.prop="indeterminate"
        :disabled="disabled"
        v-bind="inputAttrs"
        @change="$emit('update:modelValue', $event.target.checked)">
      <!-- width/height keep it out of a global :where(svg):not([width]) { width: 1em } rule. -->
      <svg viewBox="0 0 20 20"
        width="20"
        height="20"
        aria-hidden="true">
        <path class="check__tick"
          d="M5.5 10.4l3 3 6-6.6" />
        <path class="check__dash"
          d="M6 10h8" />
      </svg>
    </span>
    <span v-if="$slots.default"
      class="check__text"><slot /></span>
  </label>
</template>

<script>
// A square box for picking things: table rows, filter values. A setting that takes effect is a Field type="checkbox" switch.
export default {
  name: 'check-box',
  inheritAttrs: false,
  emits: ['update:modelValue'],
  props: {
    modelValue: { type: Boolean, default: false },
    indeterminate: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
  },
  computed: {
    // class and style dress the label; everything else (aria-label, value, …) goes to the box.
    inputAttrs() {
      const { class: _c, style: _s, ...rest } = this.$attrs
      return rest
    },
  },
}
</script>

<style lang="scss">
// The Field look in a box: tinted by --accent on hover, filled and glowing like a chosen choice capsule, the tick drawn in.
.check {
  display: inline-flex;
  align-items: flex-start;
  gap: 10rem;
  color: var(--text);
  font-size: 14rem;
  line-height: 20rem;
  vertical-align: middle;
  cursor: pointer;
  user-select: none;
}

.check__box {
  position: relative;
  flex: none;
  width: 20rem;
  height: 20rem;

  // Its own box only: the global rule otherwise lands on any input inside an element classed "check".
  > input {
    display: block;
    width: 100%;
    height: 100%;
    margin: 0;
    border: 1.5rem solid color-mix(in srgb, var(--text-muted) 55%, transparent);
    border-radius: 6rem;
    background: var(--bg-surface);
    appearance: none;
    cursor: inherit;
    transition: background-color 0.2s, border-color 0.2s, box-shadow 0.2s, transform 0.12s;

    &:checked,
    &:indeterminate {
      border-color: var(--accent);
      background: var(--accent);
      box-shadow: 0 3rem 10rem color-mix(in srgb, var(--accent) 35%, transparent);
    }

    &:active:not(:disabled) {
      transform: scale(0.88);
    }

    &:focus-visible {
      outline: 2rem solid var(--accent);
      outline-offset: 2rem;
    }
  }

  svg {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    fill: none;
    stroke: #fff;
    stroke-width: 2.2;
    stroke-linecap: round;
    stroke-linejoin: round;
    pointer-events: none;
  }

  path {
    stroke-dasharray: 14;
    stroke-dashoffset: 14;
    transition: stroke-dashoffset 0.25s ease 0.05s;
  }

  > input:checked:not(:indeterminate) + svg .check__tick,
  > input:indeterminate + svg .check__dash {
    stroke-dashoffset: 0;
  }
}

.check:hover:not(.check--disabled) .check__box > input:not(:checked):not(:indeterminate) {
  border-color: var(--accent);
  background: color-mix(in srgb, var(--accent) 12%, var(--bg-surface));
}

.check--disabled {
  cursor: not-allowed;
  opacity: 0.5;
}
</style>
